import { EMPTY_CELL_HYPHEN } from "constants/index";

import { FC, useMemo } from "react";

import { Box, BoxProps, Divider, Status, Summary, Typography } from "@portex-pro/ui-components";
import { QuoteUnionByMode } from "api/rest/quote-requests";
import toUpper from "lodash/toUpper";
import uniqBy from "lodash/uniqBy";
import { DateTime } from "luxon";
import { formatUSD } from "utils/formatCurrency";
import getTransitTimeString from "utils/getTransitTimeString";
import { renderSerializedNotes } from "utils/renderSerializedNotes";
import { toLuxonRelative } from "utils/toLuxonRelative";

type QuoteHistoryViewProps = {
  quote: QuoteUnionByMode;
  historicalQuotes: QuoteUnionByMode[];
  variant?: "default" | "compact";
};

const isTransitInQuote = (quote: QuoteUnionByMode | undefined): boolean => !!quote && "min_transit_time" in quote;

export const getQuoteValidDetails = (quote: QuoteUnionByMode): { validUntil: string; isExpired: boolean } => {
  const valid_until = quote.valid_until;

  if (!valid_until) {
    return {
      validUntil: EMPTY_CELL_HYPHEN,
      isExpired: false,
    };
  }

  const nowDt = DateTime.now();
  const validUntilDt = DateTime.fromISO(valid_until);

  const isExpiringToday = validUntilDt < nowDt.endOf("day");
  const quoteExpired = validUntilDt < nowDt;
  const validUntilFormat = isExpiringToday && !quoteExpired ? "h:mm a" : "LLL d, yyyy";

  const validUntil = DateTime.fromISO(valid_until).toFormat(validUntilFormat);

  return {
    validUntil: validUntil,
    isExpired: quoteExpired,
  };
};

const SummaryCell: FC = ({ children }) => (
  <Summary.Cell head TypographyProps={{ variant: "caption" }}>
    {typeof children === "string" ? toUpper(children) : children}
  </Summary.Cell>
);

const QuoteHistoryView: FC<QuoteHistoryViewProps> = ({ quote, historicalQuotes, variant = "default", ...props }) => {
  const displayTransitTime = isTransitInQuote(quote);
  const isCompact = variant === "compact";
  const displayHead = variant === "default";
  const boxProps: BoxProps = props;

  const brokerHistoricalQuotes = useMemo(
    () => historicalQuotes.filter((q) => q.submitter_email.toLowerCase() === quote.submitter_email.toLowerCase()),
    [historicalQuotes, quote]
  );
  const quoteHistory = useMemo<QuoteUnionByMode[]>(
    () => uniqBy([quote, ...brokerHistoricalQuotes], "_quote_id"),
    [quote, brokerHistoricalQuotes]
  );

  return (
    <Box py={isCompact ? 0 : 2} {...boxProps}>
      <Summary style={{ border: isCompact ? 0 : undefined }}>
        {displayHead ? <Summary.Head heading={"Quote History"} /> : null}
        <Summary.Item style={{ paddingTop: 0, paddingBottom: 0 }}>
          <SummaryCell>Quote Status</SummaryCell>
          <SummaryCell>Amount</SummaryCell>
          {displayTransitTime ? <SummaryCell>Est. Transit Time</SummaryCell> : null}
          <SummaryCell>Validity</SummaryCell>
          <SummaryCell>Notes</SummaryCell>
        </Summary.Item>
        <Divider />
        {quoteHistory.map((q, i, { length }) => {
          const { validUntil, isExpired } = getQuoteValidDetails(quote ?? ({} as QuoteUnionByMode));

          const quoteStatus =
            i === 0 && quote?.status === "SUBMITTED" ? (
              <Status light palette="green" uppercase>
                Active Quote
              </Status>
            ) : i === 0 && quote?.status === "BOOKED" ? (
              <Status light palette="green" uppercase>
                Booked Quote
              </Status>
            ) : (
              <Status light palette="red" uppercase>
                Inactive Quote
              </Status>
            );

          return (
            <Summary.Item key={i} line={i < length - 1}>
              <Summary.Cell head>
                {quoteStatus}
                <br />
                {q.created_at ? `${DateTime.fromISO(q.created_at).toFormat("h:mm:ss a")}` : EMPTY_CELL_HYPHEN}
                <br />
                {q.created_at ? `(${toLuxonRelative(DateTime.fromISO(q.created_at))})` : EMPTY_CELL_HYPHEN}
              </Summary.Cell>

              <Summary.Cell head>
                <Typography>{formatUSD(q.total_amount)}</Typography>
              </Summary.Cell>

              {isTransitInQuote(q) ? (
                <Summary.Cell head>
                  <Typography>
                    {getTransitTimeString(q.min_transit_time, q.max_transit_time) || EMPTY_CELL_HYPHEN}
                  </Typography>
                </Summary.Cell>
              ) : null}

              <Summary.Cell head>
                <Typography color={isExpired ? "error" : undefined}>{isExpired ? "EXPIRED" : validUntil}</Typography>
              </Summary.Cell>

              <Summary.Cell head>
                <Typography>{q.notes ? renderSerializedNotes(q.notes) : EMPTY_CELL_HYPHEN}</Typography>
              </Summary.Cell>
            </Summary.Item>
          );
        })}
      </Summary>
    </Box>
  );
};

export default QuoteHistoryView;
